/**
 * Branding (A10 epic): the tenant's own brand kit via /v1/branding/kits/current. This is the customer's brand
 * for their deliverables; it never changes the Quixo portal theme (QM-D02-003 security).
 */
import { EmptyState, ErrorState, Heading, Loading, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';

interface Kit { version: number; status: string; colors?: { name: string; hex: string; role: string }[]; fonts?: { family: string; usage: string }[]; tone?: { voice?: string } }

export function BrandingPage() {
  const { t } = useApp();
  const f = useFormat();
  const res = useResource<Kit | null>('/v1/branding/kits/current');
  let body;
  if (res.status === 'loading') body = <Loading label={t('common.loading')} />;
  else if (res.status === 'error') body = res.error instanceof BffError && res.error.status === 404 ? <EmptyState title={t('shell.screens.branding.empty')} /> : <ErrorState message={t('shell.error.load')} />;
  else if (!res.data) body = <EmptyState title={t('shell.screens.branding.empty')} />;
  else body = (
    <div className="qm-grid">
      <Panel>
        <Heading level={2}>{t('shell.screens.branding.version', { version: res.data.version })}</Heading>
        <ul className="qm-list">
          {(res.data.colors ?? []).map((c) => (
            <li key={c.hex + c.role} className="qm-list__item"><bdi>{f.text(c.name)}</bdi><span>{c.role}</span><code>{c.hex}</code></li>
          ))}
        </ul>
      </Panel>
      <Panel>
        <Heading level={2}>{t('shell.screens.branding.fonts')}</Heading>
        <ul className="qm-list">{(res.data.fonts ?? []).map((x) => <li key={x.family} className="qm-list__item"><span>{x.family}</span><span>{x.usage}</span></li>)}</ul>
        {res.data.tone?.voice ? <Text>{f.text(res.data.tone.voice)}</Text> : null}
      </Panel>
    </div>
  );
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <Heading level={1} id="page-title">{t('shell.screens.branding.title')}</Heading>
      <Text tone="muted">{t('shell.screens.branding.intro')}</Text>
      {body}
    </section>
  );
}
